// Firebase 核心功能初始化模块
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getDatabase, Database } from 'firebase/database';
import { firebaseConfig } from './config';

export interface FirebaseCore {
    app: FirebaseApp | null;
    analytics: Analytics | null;
    database: Database | null;
}

class FirebaseService {
    private static instance: FirebaseService;
    private firebase: FirebaseCore = {
        app: null,
        analytics: null,
        database: null,
    };
    private initialized = false;

    private constructor() {}

    static getInstance(): FirebaseService {
        if (!FirebaseService.instance) {
            FirebaseService.instance = new FirebaseService();
        }
        return FirebaseService.instance;
    }

    // 初始化 Firebase 核心服务
    async initialize(): Promise<FirebaseCore> {
        if (this.initialized) {
            console.log('Firebase 核心服务已经初始化');
            return this.firebase;
        }

        try {
            // 检查是否已有默认 Firebase 实例
            const existingApps = getApps();
            const defaultApp = existingApps.find(app => app.name === '[DEFAULT]');

            if (defaultApp) {
                this.firebase.app = defaultApp;
                console.log('使用现有 Firebase 默认实例');
            } else {
                this.firebase.app = initializeApp(firebaseConfig);
                console.log('创建新的 Firebase 默认实例');
            }

            // 初始化 Analytics
            try {
                this.firebase.analytics = getAnalytics(this.firebase.app);
                console.log('初始化 Firebase Analytics 成功');
            } catch (error) {
                console.error('初始化 Firebase Analytics 失败:', error);
            }

            // 初始化 Realtime Database
            try {
                this.firebase.database = getDatabase(this.firebase.app);
                console.log('初始化 Firebase Realtime Database 成功');
            } catch (error) {
                console.error('初始化 Firebase Realtime Database 失败:', error);
            }

            this.initialized = true;
            console.log('Firebase 核心服务初始化完成', this.firebase);
            
            return this.firebase;
        } catch (error) {
            console.error('初始化 Firebase 核心服务时出错:', error);
            return this.firebase;
        }
    }

    // 获取 Firebase App 实例
    getApp(): FirebaseApp | null {
        return this.firebase.app;
    }

    // 获取 Analytics 实例
    getAnalytics(): Analytics | null {
        return this.firebase.analytics;
    }

    // 获取 Database 实例
    getDatabase(): Database | null {
        return this.firebase.database;
    }

    // 获取所有 Firebase 服务
    getServices(): FirebaseCore {
        return this.firebase;
    }

    // 检查是否已初始化
    isInitialized(): boolean {
        return this.initialized;
    }
}

// 导出单例实例
export const firebaseService = FirebaseService.getInstance();

// 导出初始化函数
export async function initFirebaseCore(): Promise<FirebaseCore> {
    return firebaseService.initialize();
}

// 导出获取实例的便捷方法
export function getFirebaseApp(): FirebaseApp | null {
    return firebaseService.getApp();
}

export function getFirebaseAnalytics(): Analytics | null {
    return firebaseService.getAnalytics();
}

export function getFirebaseDatabase(): Database | null {
    return firebaseService.getDatabase();
}